import React from "react";
import {
    faDatabase,
    faPenToSquare,
    faPlug,
    faUser,
} from "@fortawesome/pro-regular-svg-icons";
import { JSONElement } from "@plugins/gatsby-source-paligo/src/types";
import { Box } from "@singlestore/fusion/components/layout";
import {
    Bold,
    Em,
    H2,
    Paragraph,
    Span,
    Strong,
} from "@singlestore/fusion/components/typography";

import {
    AccordionContent,
    AccordionItem,
    AccordionRoot,
    AccordionTrigger,
} from "components/Accordion/Accordion";
import { Admonition } from "components/Admonition/Admonition";
import { Card, CardProps } from "components/Card/Card";
import { CardGrid } from "components/Card/CardGrid";
import { CardGridContainer } from "components/Card/CardGridContainer";
import { DownloadButton } from "components/DownloadButton/DownloadButton";
import { Image, ImageCaption, ImageFigure } from "components/Image/Image";
import { InlineCode } from "components/InlineCode/InlineCode";
import { DynamicHeader } from "components/JSONParser/DynamicHeader";
import {
    LandingStepIcon,
    LandingSteps,
} from "components/LandingSteps/LandingSteps";
import { Link } from "components/Link/Link";
import { OrderedList } from "components/List/OrderedList";
import { UnorderedList } from "components/List/UnorderedList";
import { OpenAPIDisplay } from "components/OpenAPIDisplay/OpenAPIDisplay";
import { PackageButton } from "components/PackageButton/PackageButton";
import { Paragraph as CustomParagraph } from "components/Paragraph/Paragraph";
import { SqrlCallout } from "components/Sqrl/SqrlCallout";
import {
    SupportedLanguage,
    SyntaxBlockGroup,
} from "components/SyntaxBlock/SyntaxBlockGroup";
import { Table } from "components/Table/Table";
import { Text } from "components/Text/Text";
import {
    TooltipTextDefinition,
    TooltipTextDefinitionContent,
    TooltipTextDefinitionTrigger,
} from "components/Tooltip/Tooltip";

import { splitCodeBlocks } from "utils/code-utils";
import { getPlainText } from "utils/content";
import { getEnv } from "utils/get-env";
import { track } from "utils/segment";

const LANDING_STEP_ICONS = [faUser, faPenToSquare, faPlug, faDatabase];

export type PropsType = {
    jsonElement: JSONElement;
};

export function JSONParser({ jsonElement }: PropsType) {
    if (jsonElement.type === "text") {
        return <Text>{jsonElement.text}</Text>;
    }

    if (jsonElement.type === "instruction") {
        return null;
    }

    const childElements = (jsonElement?.elements || []).map(
        (element: JSONElement, index: number) => {
            // eslint-disable-next-line react/no-array-index-key
            return <JSONParser key={index} jsonElement={element} />;
        }
    );

    switch (jsonElement.name) {
        case "xml":
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return <>{childElements}</>;

        case "section": {
            const id = jsonElement?.attributes?.["xml:id"];
            return (
                <section id={id} className="section">
                    {childElements}
                </section>
            );
        }

        case "accordion-group": {
            return (
                <AccordionRoot collapsible type="single">
                    {childElements}
                </AccordionRoot>
            );
        }

        case "accordion-item": {
            const id = jsonElement?.attributes?.["xml:id"] as string;
            return (
                <AccordionItem value={id} id={id}>
                    {childElements}
                </AccordionItem>
            );
        }

        case "accordion-title": {
            return (
                <DynamicHeader jsonElement={jsonElement} showAnchor={false}>
                    <AccordionTrigger>{childElements}</AccordionTrigger>
                </DynamicHeader>
            );
        }

        case "accordion-content": {
            return <AccordionContent>{childElements}</AccordionContent>;
        }

        case "div":
        case "sidebar":
        case "collated-document":
            return <div>{childElements}</div>;

        case "title": {
            return (
                <DynamicHeader jsonElement={jsonElement}>
                    {childElements}
                </DynamicHeader>
            );
        }

        case "subtitle":
            return (
                <Paragraph
                    variant="heading-2"
                    color="mid-contrast"
                    className="subtitle"
                >
                    {childElements}
                </Paragraph>
            );

        case "para":
            return <CustomParagraph>{childElements}</CustomParagraph>;

        case "span":
            return <Span>{childElements}</Span>;

        case "note":
        case "caution":
        case "warning":
        case "tip":
        case "important":
        case "notice":
        case "danger":
            return (
                <Admonition type={jsonElement.name}>{childElements}</Admonition>
            );

        case "programlisting": {
            const roles = jsonElement?.attributes?.role?.split(" ") || [];
            const language = jsonElement?.attributes
                ?.language as Maybe<SupportedLanguage>;

            const [inputBlock, outputBlock] = splitCodeBlocks(
                getPlainText(jsonElement)
            );
            const trimNewLines = (text: string) =>
                text.replace(/(^\n*)|(\n*$)/g, "");

            return (
                <SyntaxBlockGroup
                    inputBlock={trimNewLines(inputBlock)}
                    outputBlock={trimNewLines(outputBlock)}
                    language={language}
                    interactive={roles.includes("inter")}
                />
            );
        }

        case "code":
        case "literal":
            return <InlineCode>{childElements}</InlineCode>;

        case "itemizedlist":
            return <UnorderedList>{childElements}</UnorderedList>;

        case "orderedlist":
            return <OrderedList>{childElements}</OrderedList>;

        case "listitem":
            return <li>{childElements}</li>;

        case "procedure-list":
            return <OrderedList>{childElements}</OrderedList>;

        case "procedure-sub-list":
            return <OrderedList type="a">{childElements}</OrderedList>;

        case "procedure-list-item":
            return <li>{childElements}</li>;

        case "informaltable":
            return <Table>{childElements}</Table>;

        case "colgroup":
            return <colgroup>{childElements}</colgroup>;

        case "col": {
            const width: Maybe<string> = jsonElement?.attributes?.width;
            return <col style={{ width: width }} />;
        }

        case "table": {
            // we don't show the caption for tables
            const filteredChildElements = (jsonElement?.elements || []).filter(
                ({ name }) => name !== "caption"
            );

            const childElements = filteredChildElements.map(
                (element: JSONElement, index: number) => {
                    // eslint-disable-next-line react/no-array-index-key
                    return <JSONParser key={index} jsonElement={element} />;
                }
            );

            return <Table>{childElements}</Table>;
        }

        case "thead":
            return <thead>{childElements}</thead>;

        case "tbody":
            return <tbody>{childElements}</tbody>;

        case "tr":
            return <tr>{childElements}</tr>;

        case "th":
        case "td": {
            const rowspan: Maybe<number> = jsonElement?.attributes?.rowspan
                ? Number(jsonElement.attributes.rowspan)
                : undefined;
            const colspan: Maybe<number> = jsonElement?.attributes?.colspan
                ? Number(jsonElement.attributes.colspan)
                : undefined;

            return (
                <jsonElement.name rowSpan={rowspan} colSpan={colspan}>
                    {childElements}
                </jsonElement.name>
            );
        }

        case "emphasis": {
            const role = jsonElement?.attributes?.role;

            if (role === "strong") {
                return <Strong>{childElements}</Strong>;
            } else if (role === "bold") {
                return <Bold>{childElements}</Bold>;
            }

            return <Em>{childElements}</Em>;
        }

        case "quote":
            return <q>{childElements}</q>;

        case "guilabel":
            return <Strong>{childElements}</Strong>;

        case "property":
            return <Span>{childElements}</Span>;

        case "link": {
            const href = jsonElement?.attributes?.href;

            if (!href) {
                return <span>{childElements}</span>;
            }

            const handleClick = () => {
                track("UI Element Clicked", {
                    elementName: href,
                    elementType: "Content Link",
                    destination: href,
                });
            };

            return (
                <Link to={href} onClick={handleClick}>
                    {childElements}
                </Link>
            );
        }

        case "download-button": {
            const href = jsonElement?.attributes?.href;

            if (!href) {
                return <span>{childElements}</span>;
            }

            const handleClick = () => {
                track("UI Element Clicked", {
                    elementName: href,
                    elementType: "Content Link",
                    elementContext: "Download Button",
                    destination: href,
                });
            };

            return (
                <DownloadButton to={href} onClick={handleClick}>
                    {childElements}
                </DownloadButton>
            );
        }

        case "block-link": {
            const href = jsonElement?.attributes?.href;
            const textLabel = jsonElement?.attributes?.textLabel;

            if (!href) {
                return <div>{childElements}</div>;
            }

            const handleClick = () => {
                track("UI Element Clicked", {
                    elementName: textLabel,
                    elementType: "Content Link",
                    destination: href,
                });
            };

            return (
                <Link
                    to={href}
                    styled={false}
                    aria-label={textLabel}
                    onClick={handleClick}
                >
                    {childElements}
                </Link>
            );
        }

        case "download-release-link": {
            const target = jsonElement?.attributes?.target;
            const releaseJson = jsonElement?.meta?.releaseJson;
            const releaseUrl = jsonElement?.meta?.releaseUrl;

            if (!target || !releaseJson || !releaseUrl) {
                throw new Error(
                    "Missing required attributes for download button"
                );
            }

            return (
                <PackageButton
                    target={target}
                    releaseJson={releaseJson}
                    releaseUrl={releaseUrl}
                >
                    {childElements}
                </PackageButton>
            );
        }

        case "informalfigure":
        case "figure": {
            const id = jsonElement?.attributes?.["xml:id"];
            return <ImageFigure id={id}>{childElements}</ImageFigure>;
        }

        case "figuretitle":
            return (
                <Paragraph variant="heading-1" color="mid-contrast">
                    {childElements}
                </Paragraph>
            );

        case "mediaobject":
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return <>{childElements}</>;

        case "imageobject":
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return <>{childElements}</>;

        case "imagedata":
            throw new Error("`imagedata` element type not supported");

        case "caption":
            return <ImageCaption>{childElements}</ImageCaption>;

        case "image": {
            const roles = jsonElement.attributes!.roles?.split(" ") || [];

            return (
                <Image
                    src={jsonElement.attributes!.src!}
                    alt={jsonElement.attributes!.alt}
                    dimension={{
                        width: Number(jsonElement.attributes?.width),
                        height: Number(jsonElement.attributes?.height),
                    }}
                    halfWidth={roles.includes("width-half")}
                />
            );
        }

        case "glossary":
            return <div>{childElements}</div>;

        case "glossentry":
            return <div>{childElements}</div>;

        case "glossary-term-title":
            return <H2 variant="heading-2">{childElements}</H2>;

        case "glossdef":
            return <Box>{childElements}</Box>;

        case "glossary-term-reference":
            return (
                <TooltipTextDefinition>{childElements}</TooltipTextDefinition>
            );

        case "glossary-term-reference-text":
            return (
                <TooltipTextDefinitionTrigger>
                    {childElements}
                </TooltipTextDefinitionTrigger>
            );

        case "glossary-term-reference-def":
            return (
                <TooltipTextDefinitionContent>
                    {childElements}
                </TooltipTextDefinitionContent>
            );

        case "card-grid-section":
            return <CardGridContainer>{childElements}</CardGridContainer>;

        case "card-grid": {
            // must be set from ingesting step
            const cardSize = jsonElement?.attributes?.cardSize;

            const cols = cardSize === "cards-small" ? 4 : 2;

            return <CardGrid cols={cols}>{childElements}</CardGrid>;
        }

        case "card": {
            const align = (jsonElement?.attributes?.align ||
                "left") as CardProps["textAlign"];

            // cardSize manually set in parse-content
            const cardSize = jsonElement?.attributes
                ?.cardSize as CardProps["cardSize"];

            return (
                <Card textAlign={align} cardSize={cardSize}>
                    {childElements}
                </Card>
            );
        }

        case "card-title":
            return (
                <H2 variant="heading-2" className="card-title">
                    {childElements}
                </H2>
            );

        case "landing-section-title":
            return <H2 variant="heading-4">{childElements}</H2>;

        case "landing-steps":
            return <LandingSteps>{childElements}</LandingSteps>;

        case "landing-step":
            return <div className="step">{childElements}</div>;

        case "landing-step-title":
            return (
                <Paragraph variant="heading-2" className="landing-step-title">
                    {childElements}
                </Paragraph>
            );

        case "landing-step-icon": {
            const index = parseInt(jsonElement?.attributes?.index as string);
            return <LandingStepIcon icon={LANDING_STEP_ICONS[index]} />;
        }

        case "landing-step-text-container":
            return <div className="text-container">{childElements}</div>;

        case "api-spec": {
            const env = getEnv();
            const href =
                env === "development"
                    ? "http://localhost:8080/spec"
                    : (jsonElement.attributes!.href as string);

            return <OpenAPIDisplay href={href} />;
        }

        case "variable-list": {
            return <div>{childElements}</div>;
        }

        case "variable-list-entry": {
            return <div className="variable-list-entry">{childElements}</div>;
        }

        case "variable-list-name": {
            return (
                <Paragraph variant="heading-2" color="high-contrast">
                    {childElements}
                </Paragraph>
            );
        }

        case "variable-list-value": {
            return (
                <Paragraph className="variable-list-value" pl="2x">
                    {childElements}
                </Paragraph>
            );
        }

        case "sqrl-callout": {
            return <SqrlCallout />;
        }

        default:
            // console.log("NOT FOUND", jsonElement);
            return null;
    }
}
